import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createContext, useContextSelector } from 'use-context-selector';
import { useLazyQuery } from '@apollo/client';

import { createClientsReportApolloClient } from '@seller-area/contexts/clients-report/apollo-client';
import { useAuth } from '@seller-area/wrappers/auth-wrapper';

import { UNITS_REPORT_QUERY } from './queries';
import { buildUnitsReportGraphqlVariables } from './build-report-params';
import { EMPTY_NULL_KPIS, UnitsNullKpiKey } from './null-kpi-config';
import {
  UnitConsumidora,
  UnitsConsumidorasContextType,
  UnitsConsumidorasFilters,
  UnitsReportIndicators,
} from './types';

const DEFAULT_INDICATORS: UnitsReportIndicators = {
  totalUnits: 0,
  totalVolumeMwm: 0,
  nullKpis: EMPTY_NULL_KPIS,
};

const DEFAULT_FILTERS: UnitsConsumidorasFilters = {
  term: '',
  nameFilter: '',
  legalNameFilter: '',
  accountManagerIdFilter: null,
  crmIdFilter: '',
  unitIdFilter: '',
  companyIdFilter: '',
  docNumberFilter: '',
  cityFilter: '',
  addressFilter: '',
  postalCodeFilter: '',
  installationCodeFilter: '',
  newInstallationCodeFilter: '',
  edcCustomerCodeFilter: '',
  assetCodeFilter: '',
  cceeProfileFilter: '',
  status: 2,
  submarket: null,
  productType: null,
  energyType: null,
  unitType: null,
  tariffSubgroup: null,
  tariffModality: null,
  energyContractingModel: null,
  docType: null,
  edc: null,
  exposure: null,
  state: null,
  aclBefore2025Law: null,
  isNewConnection: null,
  powerGenerator: null,
  volumeMwmRange: null,
  exposurePercentageRange: null,
  managementCostRange: null,
  demandPeakRange: null,
  demandOffPeakRange: null,
  demandPeakBeforeRange: null,
  demandOffPeakBeforeRange: null,
  demandPeakBeforeLawRange: null,
  demandOffPeakBeforeLawRange: null,
  generatorCostRange: null,
  migrationDateRange: null,
  expectedMigrationDateRange: null,
  clientSinceRange: null,
  churnDateRange: null,
  createdAtRange: null,
  updatedAtRange: null,
};

const GATEWAY_SCHEMA_HINT =
  'Se estiver usando o Gateway, publique o schema (rover) após o deploy do backend. Em local, use VITE_CUSTOMER_API_GRAPHQL apontando para a customer API.';

export const UnitsConsumidorasContext = createContext<UnitsConsumidorasContextType>({
  units: [],
  indicators: DEFAULT_INDICATORS,
  total: 0,
  loading: false,
  queryError: null,
  page: 0,
  limit: 50,
  filters: DEFAULT_FILTERS,
  sortBy: null,
  sortOrder: 'asc',
  setPage: () => undefined,
  setLimit: () => undefined,
  setSort: () => undefined,
  setFilter: () => undefined,
  clearFilter: () => undefined,
  clearAllFilters: () => undefined,
  toggleNullKpi: () => undefined,
  activeNullKpi: null,
  fetchUnits: () => undefined,
});

export const UnitsConsumidorasProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { loading: authLoading, token } = useAuth();
  const tokenRef = useRef(token);
  tokenRef.current = token;

  const reportClient = useMemo(() => createClientsReportApolloClient(tokenRef), []);

  const [page, setPageState] = useState(0);
  const [limit, setLimitState] = useState(50);
  const [filters, setFiltersState] = useState<UnitsConsumidorasFilters>(DEFAULT_FILTERS);
  const [units, setUnits] = useState<UnitConsumidora[]>([]);
  const [total, setTotal] = useState(0);
  const [indicators, setIndicators] = useState<UnitsReportIndicators>(DEFAULT_INDICATORS);
  const [queryError, setQueryError] = useState<string | null>(null);
  const [sortBy, setSortByState] = useState<string | null>(null);
  const [sortOrder, setSortOrderState] = useState<'asc' | 'desc'>('asc');
  const [activeNullKpi, setActiveNullKpi] = useState<UnitsNullKpiKey | null>(null);

  const [runQuery, { loading }] = useLazyQuery(UNITS_REPORT_QUERY, {
    client: reportClient,
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const report = data?.unitsReport;
      if (report) {
        setQueryError(null);
        setUnits(report.data ?? []);
        setTotal(report.total ?? 0);
        setIndicators(report.indicators ?? DEFAULT_INDICATORS);
      }
    },
    onError: (error) => {
      setUnits([]);
      setTotal(0);
      setIndicators(DEFAULT_INDICATORS);
      const message = error.message?.includes('unitsReport')
        ? `Query unitsReport indisponível no GraphQL configurado. ${GATEWAY_SCHEMA_HINT}`
        : error.message || 'Não foi possível carregar o relatório de UCs.';
      setQueryError(message);
    },
  });

  const runQueryRef = useRef(runQuery);
  runQueryRef.current = runQuery;

  const fetchUnits = useCallback(() => {
    runQueryRef.current({
      variables: buildUnitsReportGraphqlVariables(page, limit, filters, sortBy, sortOrder, activeNullKpi),
    });
  }, [page, limit, filters, sortBy, sortOrder, activeNullKpi]);

  useEffect(() => {
    if (authLoading || !token) return;

    runQueryRef.current({
      variables: buildUnitsReportGraphqlVariables(page, limit, filters, sortBy, sortOrder, activeNullKpi),
    });
  }, [authLoading, token, page, limit, filters, sortBy, sortOrder, activeNullKpi]);

  const setSort = useCallback((col: string, order: 'asc' | 'desc') => {
    setSortByState(col);
    setSortOrderState(order);
    setPageState(0);
  }, []);

  const setPage = useCallback((p: number) => setPageState(p), []);
  const setLimit = useCallback((l: number) => {
    setLimitState(l);
    setPageState(0);
  }, []);

  const setFilter = useCallback((key: keyof UnitsConsumidorasFilters, value: unknown) => {
    setFiltersState((prev) => ({ ...prev, [key]: value }));
    setPageState(0);
  }, []);

  const clearFilter = useCallback((key: keyof UnitsConsumidorasFilters) => {
    setFiltersState((prev) => ({ ...prev, [key]: DEFAULT_FILTERS[key] }));
    setPageState(0);
  }, []);

  const clearAllFilters = useCallback(() => {
    setFiltersState(DEFAULT_FILTERS);
    setSortByState(null);
    setSortOrderState('asc');
    setActiveNullKpi(null);
    setPageState(0);
  }, []);

  const toggleNullKpi = useCallback((key: UnitsNullKpiKey) => {
    setActiveNullKpi((prev) => (prev === key ? null : key));
    setPageState(0);
  }, []);

  const value = useMemo(
    () => ({
      units,
      indicators,
      total,
      loading: loading || authLoading,
      queryError,
      page,
      limit,
      filters,
      activeNullKpi,
      sortBy,
      sortOrder,
      setPage,
      setLimit,
      setSort,
      setFilter,
      clearFilter,
      clearAllFilters,
      toggleNullKpi,
      fetchUnits,
    }),
    [
      units,
      indicators,
      total,
      loading,
      authLoading,
      queryError,
      page,
      limit,
      filters,
      activeNullKpi,
      sortBy,
      sortOrder,
      setPage,
      setLimit,
      setSort,
      setFilter,
      clearFilter,
      clearAllFilters,
      toggleNullKpi,
      fetchUnits,
    ],
  );

  return <UnitsConsumidorasContext.Provider value={value}>{children}</UnitsConsumidorasContext.Provider>;
};

export const useUnitsConsumidoras = () => useContextSelector(UnitsConsumidorasContext, (c) => c);
